import React, { useEffect, useState } from "react";
import { Button } from "@/shared/components/ui/button";
import api from "@/shared/utils/api";
import {
  ACCENT_BUTTON_CLASS,
  CONSENT_CHANGED_EVENT,
  currentPagePath,
  ENDPOINT,
} from "./consentConstants";
import type { ConsentSaveResponse, ConsentState } from "./consentTypes";
import { OPEN_EVENT } from "./PrivacyChoicesModal";

const GENERIC_ERROR = "We couldn't save your choices. Please try again.";

export interface GdprBannerProps {
  initialState?: ConsentState | null;
}

const GdprBanner: React.FC<GdprBannerProps> = ({ initialState = null }) => {
  const [visible, setVisible] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const dismiss = () => setVisible(false);
    document.addEventListener(CONSENT_CHANGED_EVENT, dismiss);
    return () => document.removeEventListener(CONSENT_CHANGED_EVENT, dismiss);
  }, []);

  const persist = async (analytics: boolean, advertising: boolean) => {
    setSaving(true);
    setError(null);
    try {
      const payload = (await api.post(ENDPOINT, {
        preferences: { analytics, advertising },
        pagePath: currentPagePath(),
        source: "banner",
        ...(initialState?.schoolId != null
          ? { schoolId: initialState.schoolId }
          : {}),
      })) as ConsentSaveResponse;

      if (!payload?.success) {
        setError(GENERIC_ERROR);
        return;
      }

      window.gcConsentRuntime?.apply({
        ...(initialState ?? {}),
        preferences: { analytics, advertising },
        jurisdiction: payload.data?.jurisdiction ?? initialState?.jurisdiction,
        gpcDetected: payload.data?.gpcDetected ?? initialState?.gpcDetected ?? false,
        setAt: payload.data?.setAt ?? new Date().toISOString(),
      });
      setVisible(false);
    } catch {
      setError(GENERIC_ERROR);
    } finally {
      setSaving(false);
    }
  };

  const openModal = () => {
    document.dispatchEvent(new CustomEvent(OPEN_EVENT));
  };

  if (!visible) return null;

  return (
    <section
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[1030] border-t border-gray-200 bg-white shadow-lg"
    >
      <div className="container mx-auto flex flex-col gap-4 px-4 py-4 md:flex-row md:items-center md:justify-between">
        <div className="text-gray-700">
          <p className="text-base font-semibold text-gray-900">
            We value your privacy
          </p>
          <p className="mt-1 text-sm">
            We use cookies to run this site and, with your consent, for
            analytics and marketing. Choose how your data is used.
          </p>
          {error && (
            <p role="alert" className="mt-1 text-sm text-primary-red">
              {error}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-col gap-2 md:flex-row md:items-center">
          <Button
            variant="primary"
            onClick={() => persist(true, true)}
            disabled={saving}
            className={`${ACCENT_BUTTON_CLASS} w-full rounded-lg md:order-3 md:w-auto`}
          >
            Accept all
          </Button>
          <Button
            variant="neutral"
            onClick={() => persist(false, false)}
            disabled={saving}
            className="w-full rounded-lg md:order-1 md:w-auto"
          >
            Reject all
          </Button>
          <Button
            variant="neutral"
            onClick={openModal}
            disabled={saving}
            className="w-full rounded-lg md:order-2 md:w-auto"
          >
            Manage choices
          </Button>
        </div>
      </div>
    </section>
  );
};

export default GdprBanner;
